import type { Subscription } from 'rxjs';
import { Subject } from 'rxjs';

import { BlockerUtil } from '../components/app/Blocker/Blocker';

import type { ApiError } from './api-error';
import { apiRequest } from './api-request';
import type { RequestProps } from './api.types';

type RequestFn = typeof apiRequest;

export type FetchWatcher<ReturnType> = {
  refetch: (
    fireInvalidateEvent?: boolean,
    bypassBlocker?: boolean
  ) => Promise<ReturnType>;
  getData: () => ReturnType | null;
  getError: () => ApiError;
  hasError: () => boolean;
  hasResolved: () => boolean;
  listen: (evt: (data: ReturnType) => void) => Subscription;
  listenForInvalidate: (evt: () => void) => Subscription;
  toPromise: () => Promise<ReturnType>;
};

export class EndpointCache<ReturnType> {
  private resolved = false;

  private response: unknown = null;

  private errorResponse?: ApiError | null = null;

  private prevResponse?: unknown = null;

  private nextRequestProps?: RequestProps | null = null;

  private requestProps?: RequestProps | null = null;

  private prevRequestProps?: RequestProps | null = null;

  private subject = new Subject<ReturnType | null>();

  private invalidateListener = new Subject<null | (() => void)>();

  private requestFn: RequestFn;

  constructor(requestFn: RequestFn = apiRequest) {
    this.requestFn = requestFn;
  }

  private async watchRequest(nextRequestProps?: RequestProps | null) {
    if (
      !this.nextRequestProps ||
      this.resolved ||
      this.getComparableResult(nextRequestProps) !==
        this.getComparableResult(this.nextRequestProps)
    ) {
      this.resolved = false;
      this.nextRequestProps = nextRequestProps;
      if (!nextRequestProps) {
        throw new Error('Unknown network request');
      }
      return this.requestFn<ReturnType>(nextRequestProps)
        .then(data => {
          if (
            this.getComparableResult(nextRequestProps) ===
            this.getComparableResult(this.nextRequestProps)
          ) {
            this.nextRequestProps = null;
            this.resolved = true;
            this.response = data;
            this.requestProps = nextRequestProps;
            this.subject.next(data);
            /**
             * React's defer value seems to request old and new values
             * in a weird order, so this clears out data from old request after
             * everything settles better
             */

            setTimeout(() => {
              this.prevRequestProps = null;
              this.prevResponse = null;
              /**
               * Note to Brutus in the future:
               * We were still getting prev requests after new response finished and
               * prevRequest cleared.
               *
               * So it doesn't look like we can just put it outside the
               * lifecycle with a 0 timeout. Doing 1 sec here because it seems reasonable
               * that if someone clicks back and forth quickly,
               * we should just give them cache anyway, so that they don't spam our servers.
               *
               * If this doesn't work, we'll need to look at a full cache clearing
               * mechanism based on a TTD - Time To Donut (Anew)
               */
            }, 1000);
            return data;
          }
          return this.response;
        })
        .catch(e => {
          if (
            this.getComparableResult(nextRequestProps) ===
            this.getComparableResult(this.nextRequestProps)
          ) {
            this.errorResponse = e;
            this.subject.next(null);
          }
        });
    }

    return null;
  }

  async invalidate(fireInvalidateEvent = true, bypassBlocker = false) {
    if (this.resolved) {
      this.prevRequestProps = this.requestProps;
      this.prevResponse = this.response;
      this.response = null;
      this.requestProps = null;
    }

    const blockerEvent = bypassBlocker ? null : BlockerUtil.block();

    try {
      const data = await this.watchRequest(this.prevRequestProps);
      if (blockerEvent) {
        BlockerUtil.unblock(blockerEvent);
      }
      if (fireInvalidateEvent) {
        this.invalidateListener.next(null);
      }
      return data;
    } catch (e) {
      if (blockerEvent) {
        BlockerUtil.unblock(blockerEvent);
      }
      if (fireInvalidateEvent) {
        this.invalidateListener.next(null);
      }
      throw e;
    }
  }

  clear() {
    this.prevRequestProps = null;
    this.prevResponse = null;
    this.response = null;
    this.requestProps = null;
    this.resolved = false;
    this.errorResponse = null;
    this.nextRequestProps = null;
  }

  private getComparableResult(request?: RequestProps | null) {
    const query: Record<string, unknown> | undefined =
      request?.method === 'POST'
        ? (request.body as Record<string, unknown>)
        : request?.query;
    if (query === undefined) {
      return query;
    }

    try {
      return JSON.stringify(
        Object.keys(query)
          .sort()
          .reduce(function (acc: Record<string, unknown>, key: string) {
            acc[key] = query[key];
            return acc;
          }, {})
      );
    } catch {
      return query;
    }
  }

  hasResolved() {
    return !!(this.resolved || this.errorResponse);
  }

  getError() {
    return this.errorResponse;
  }

  getData(requestParams: RequestProps) {
    let foundResponse: unknown = null;
    if (
      this.requestProps &&
      this.getComparableResult(requestParams) ===
        this.getComparableResult(this.requestProps)
    ) {
      foundResponse = this.response;
    } else if (
      this.prevRequestProps &&
      this.getComparableResult(requestParams) ===
        this.getComparableResult(this.prevRequestProps)
    ) {
      foundResponse = this.prevResponse;
    }

    return foundResponse;
  }

  watch(requestParams: RequestProps) {
    if (!this.getData(requestParams)) {
      if (this.requestProps) {
        this.prevRequestProps = this.requestProps;
        this.prevResponse = this.response;
      }

      void this.watchRequest(requestParams);
    }

    const FetchWatcherApi = {
      hasResolved: () => {
        return this.hasResolved();
      },
      getData: () => {
        return this.getData(requestParams);
      },
      hasError: () => {
        return !!this.getError();
      },
      getError: () => {
        return this.getError();
      },
      toPromise: async () => {
        return new Promise(resolve => {
          FetchWatcherApi.listen((data: ReturnType) => {
            resolve(data);
          });
        });
      },
      listen: (evt: (data: ReturnType | null) => void) => {
        return this.subject.subscribe({
          next: (data: ReturnType | null) => {
            evt(data);
          },
        });
      },
      listenForInvalidate: (evt: () => void) => {
        return this.invalidateListener.subscribe(evt);
      },
      refetch: async (
        fireInvalidateEvent?: boolean,
        bypassBlocker?: boolean
      ) => {
        return this.invalidate(fireInvalidateEvent, bypassBlocker);
      },
    } as const as FetchWatcher<ReturnType>;

    return FetchWatcherApi;
  }
}
